import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PhoneCall, CheckCircle2, Clock, Circle, ExternalLink } from "lucide-react";
import { api, formatApiError, type TelephonyGoLiveStep } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardHeader, CardTitle, CardContent, Select, StatusBadge, LoadingBlock, ErrorState, toast } from "@/components/ds";

const OVERALL_TONE: Record<string, string> = { live: "green", configuration_required: "amber", not_configured: "slate" };
const OVERALL_LABEL: Record<string, string> = { live: "Live", configuration_required: "Configuration required", not_configured: "Not connected" };
const MILESTONE_OPTS = [
  { value: "not_started", label: "Not started" },
  { value: "submitted", label: "Submitted / pending" },
  { value: "approved", label: "Approved" },
];

function StepIcon({ s }: { s: string }) {
  if (s === "done") return <CheckCircle2 className="h-5 w-5 text-emerald-600" />;
  if (s === "in_progress") return <Clock className="h-5 w-5 text-amber-500" />;
  return <Circle className="h-5 w-5 text-slate-300" />;
}

export function TelephonyGoLivePanel() {
  const qc = useQueryClient();
  const { role } = useAuth();
  const isOwner = role === "owner";
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["telephony-go-live"], queryFn: api.telephonyGoLive });
  const update = useMutation({
    mutationFn: (b: { kyc_status?: string; a2p_status?: string }) => api.telephonyGoLiveUpdate(b),
    onSuccess: () => { toast.success("Compliance status updated"); qc.invalidateQueries({ queryKey: ["telephony-go-live"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  if (isLoading) return <LoadingBlock rows={4} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;

  const milestone = (s: TelephonyGoLiveStep) =>
    s.key === "trust_hub_kyc" ? "kyc_status" : s.key === "a2p_10dlc" ? "a2p_status" : null;

  return (
    <Card data-testid="telephony-golive">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><PhoneCall className="h-5 w-5 text-blue-600" /> Telephony Go-Live</CardTitle>
        <StatusBadge tone={OVERALL_TONE[data.overall] as any} data-testid="telephony-overall">{OVERALL_LABEL[data.overall]}</StatusBadge>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="telephony-message">{data.message}</p>
        <p className="text-xs text-slate-400">
          Twilio SMS &amp; voice stay safely dormant until every step below is complete — nothing is simulated. Server
          credentials, webhook URL and your number are detected automatically; you track the Twilio compliance milestones.
        </p>
        <ul className="divide-y divide-slate-100">
          {data.steps.map((s) => {
            const m = milestone(s);
            return (
              <li key={s.key} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`telephony-step-${s.key}`}>
                <div className="flex items-start gap-3">
                  <StepIcon s={s.status} />
                  <div>
                    <p className="text-sm font-medium text-slate-900">{s.label}</p>
                    {s.detail && <p className="text-xs text-slate-500">{s.detail}</p>}
                  </div>
                </div>
                {m ? (
                  <div className="w-full sm:w-48">
                    {isOwner ? (
                      <Select value={s.value || "not_started"} options={MILESTONE_OPTS}
                        onChange={(v) => update.mutate({ [m]: v })} testid={`telephony-select-${s.key}`} />
                    ) : (
                      <StatusBadge tone="slate">{(s.value || "not_started").replace(/_/g, " ")}</StatusBadge>
                    )}
                  </div>
                ) : (
                  <StatusBadge tone={s.status === "done" ? "green" : "slate"}>{s.status === "done" ? "Detected" : "Pending"}</StatusBadge>
                )}
              </li>
            );
          })}
        </ul>
        <div className="flex flex-wrap gap-3 text-xs">
          <a href="https://console.twilio.com/us1/account/trust-hub" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700" data-testid="telephony-link-kyc">Twilio Trust Hub (KYC) <ExternalLink className="h-3 w-3" /></a>
          <a href="https://console.twilio.com/us1/develop/sms/regulatory-compliance/a2p-10dlc" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700" data-testid="telephony-link-a2p">A2P 10DLC registration <ExternalLink className="h-3 w-3" /></a>
        </div>
        {!isOwner && <p className="text-xs text-slate-400">Only the organization owner can update compliance status.</p>}
      </CardContent>
    </Card>
  );
}
